import {
    createContext,
    createSignal,
    onMount,
    useContext,
} from 'solid-js'

import {
    optIn as posthogOptIn,
    optOut as posthogOptOut,
} from '../../utilities/use-posthog.js'
import { updateGtagConsent } from '../../utilities/use-analytics.js'
import {
    disableSentryReplay,
    enableSentryReplay,
} from '../../utilities/use-sentry.js'
import {
    CONSENT_STATE,
    clearStoredConsent,
    readStoredConsent,
    writeStoredConsent,
} from '../../utilities/consent-storage.js'

/**
 * Cookie / analytics consent context.
 *
 * MVP: opt-IN by default — PostHog fires for everyone unless they actively
 * Decline on the banner. Choice is persisted in localStorage. After Accept
 * or Decline is recorded, the banner never reappears on its own; a future
 * "Cookie preferences" footer link will let users change their mind (see
 * the TODO above `revokeAndReopen()` below).
 *
 * This single "We value your privacy" banner now governs BOTH PostHog and
 * Sentry Session Replay: Accept/Decline starts/stops replay.
 * GA4 and the ad pixels are not part of this MVP scope; see the project
 * plan's pinned items for the deferred follow-up that extends gating to
 * those trackers.
 */
const ConsentContext = createContext()

export function ConsentProvider (props) {
    const [
        consentState,
        setConsentState,
    ] = createSignal(CONSENT_STATE.UNKNOWN)
    const [
        showBanner,
        setShowBanner,
    ] = createSignal(false)

    onMount(() => {
        const stored = readStoredConsent()
        setConsentState(stored)
        switch (stored) {
            case CONSENT_STATE.ACCEPTED: {
                // No action needed. initSentry() and initPostHog() run FIRST —
                // they're registered in the root render's onMount in main.jsx,
                // which fires before this child provider's onMount — and both
                // already honor accepted consent: initSentry() resumes replay
                // buffering and initPostHog() is called with optOutByDefault=false
                // so PostHog captures. Re-asserting either here is redundant.
                //
                // gtag is NOT redundant here, unlike the two above. It is a
                // no-op only while index.html declares 'granted' defaults; if
                // those are ever flipped to 'denied' (the GDPR/Law 25 path the
                // comment there describes) this call is what keeps an
                // already-accepted user granted across reloads. Asserting it
                // unconditionally keeps this file correct under either default,
                // instead of silently depending on the HTML.
                updateGtagConsent(true)
                break
            }
            case CONSENT_STATE.REJECTED: {
                // Defense-in-depth: re-assert the PostHog opt-out on every boot.
                // initPostHog({ optOutByDefault: true }) only opts out when
                // PostHog has NO stored consent of its own; a stale persisted
                // opt-in (PostHog's `__ph_opt_in_out_*` entry diverging from our
                // localStorage consent key — e.g. one store cleared but not the
                // other) would otherwise keep capturing. opt_out_capturing() is
                // idempotent. No Sentry call belongs here: initSentry() already
                // re-derives replay state from stored consent on boot and stopped
                // it, so a second stop() would be pure redundancy.
                posthogOptOut()
                // Google Consent Mode defaults are declared 'granted' in
                // index.html (opt-out model), so a stored rejection MUST be
                // re-asserted on every boot or GA4/Ads/GTM would keep the
                // granted defaults for a user who already declined.
                updateGtagConsent(false)
                break
            }
            default: {
                // UNKNOWN — opt-in: replay stays off until the user accepts;
                // just show the banner so they can choose.
                setShowBanner(true)
                break
            }
        }
    })

    const accept = () => {
        writeStoredConsent(CONSENT_STATE.ACCEPTED)
        setConsentState(CONSENT_STATE.ACCEPTED)
        setShowBanner(false)
        posthogOptIn()
        enableSentryReplay()
        updateGtagConsent(true)
    }

    const decline = () => {
        writeStoredConsent(CONSENT_STATE.REJECTED)
        setConsentState(CONSENT_STATE.REJECTED)
        setShowBanner(false)
        posthogOptOut()
        disableSentryReplay()
        updateGtagConsent(false)
    }

    // TODO: Wire revokeAndReopen() to a "Cookie preferences" link in the
    // footer (footer-organism third column, alongside Terms / Privacy /
    // Contact) so users can change their mind without clearing browser
    // storage. Deferred from MVP scope. Becomes important if Lodgr ever
    // flips to strict opt-in for EU users (GDPR Article 7(3): withdrawal
    // must be as easy as granting). See the consent-banner MVP plan's
    // "Pinned items" section for context.
    const revokeAndReopen = () => {
        clearStoredConsent()
        setConsentState(CONSENT_STATE.UNKNOWN)
        setShowBanner(true)
        disableSentryReplay()
        // Back to UNKNOWN. Under the opt-out model that means the granted
        // defaults apply again, matching how PostHog/Sentry are left here.
        updateGtagConsent(true)
    }

    const api = {
        accept,
        consentState,
        decline,
        revokeAndReopen,
        showBanner,
    }

    return (
        <ConsentContext.Provider value={api}>
            {props.children}
        </ConsentContext.Provider>
    )
}

export const useConsent = () => {
    const context = useContext(ConsentContext)
    if (!context) {
        throw new Error('useConsent must be used within a ConsentProvider')
    }
    return context
}

export {
    CONSENT_STATE,
    CONSENT_STORAGE_KEY,
    readStoredConsent,
} from '../../utilities/consent-storage.js'
